import { useEffect, useState } from "react";
import type { AssetComponentModule, PrefetchAssetsModule } from "../types";
import { loadAssetRenderingService } from "../loader";

interface UseAssetRendererResult {
  isLoading: boolean;
  isReady: boolean;
  error: string | null;
  AssetComponent: AssetComponentModule["Asset"] | null;
  prefetch: PrefetchAssetsModule["prefetch"] | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  remoteReact: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  remoteReactDOM: any;
}

export function useAssetRenderer(env?: string): UseAssetRendererResult {
  const [state, setState] = useState<{
    isLoading: boolean;
    error: string | null;
    assetModule: AssetComponentModule | null;
    prefetchModule: PrefetchAssetsModule | null;
    remoteReact: unknown;
    remoteReactDOM: unknown;
  }>({
    isLoading: true,
    error: null,
    assetModule: null,
    prefetchModule: null,
    remoteReact: null,
    remoteReactDOM: null,
  });

  useEffect(() => {
    let cancelled = false;

    loadAssetRenderingService(env)
      .then(({ assetModule, prefetchModule, remoteReact, remoteReactDOM }) => {
        if (!cancelled) {
          setState({
            isLoading: false,
            error: null,
            assetModule,
            prefetchModule,
            remoteReact,
            remoteReactDOM,
          });
        }
      })
      .catch((err: Error) => {
        console.error("[AssetRenderer] Failed to load:", err);
        if (!cancelled) {
          setState({
            isLoading: false,
            error: err.message,
            assetModule: null,
            prefetchModule: null,
            remoteReact: null,
            remoteReactDOM: null,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [env]);

  return {
    isLoading: state.isLoading,
    isReady: !state.isLoading && !state.error && !!state.assetModule,
    error: state.error,
    AssetComponent: state.assetModule?.Asset ?? null,
    prefetch: state.prefetchModule?.prefetch ?? null,
    remoteReact: state.remoteReact,
    remoteReactDOM: state.remoteReactDOM,
  };
}
